import { Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';

import { createLogger } from '../utils/logger';
const logger = createLogger('version');

/**
 * Version Configuration
 * Centralized version management for the app
 */

export interface VersionInfo {
  version: string; // Semantic version (1.0.0)
  buildNumber: string; // Build number (iOS: CFBundleVersion, Android: versionCode)
  bundleId: string; // App bundle identifier
  appName: string; // App display name
  environment: 'development' | 'staging' | 'production';
}

export interface MinimumVersionRequirement {
  ios: string;
  android: string;
  apiVersion: string; // Minimum backend API version
}

class VersionConfig {
  /**
   * Get current app version info
   */
  async getVersionInfo(): Promise<VersionInfo> {
    try {
      const version = DeviceInfo.getVersion();
      const buildNumber = DeviceInfo.getBuildNumber();
      const bundleId = DeviceInfo.getBundleId();
      const appName = DeviceInfo.getApplicationName();

      return {
        version,
        buildNumber,
        bundleId,
        appName,
        environment: this.getEnvironment(),
      };
    } catch (error) {
      logger.error('Failed to get version info:', error);
      // Fallback to package.json values
      return {
        version: '0.0.1',
        buildNumber: '1',
        bundleId: 'com.myfinalkey',
        appName: 'MyFinalKey',
        environment: this.getEnvironment(),
      };
    }
  }

  /**
   * Get formatted version string for display
   */
  async getVersionString(): Promise<string> {
    const info = await this.getVersionInfo();
    if (__DEV__) {
      return `v${info.version} (${info.buildNumber}) - ${info.environment}`;
    }
    return `v${info.version}`;
  }

  /**
   * Get full version details string
   */
  async getFullVersionString(): Promise<string> {
    const info = await this.getVersionInfo();
    return `Version ${info.version}\nBuild ${info.buildNumber}\nEnvironment: ${info.environment}\nPlatform: ${Platform.OS} ${Platform.Version}`;
  }

  /**
   * Determine current environment
   */
  getEnvironment(): 'development' | 'staging' | 'production' {
    if (__DEV__) {
      return 'development';
    }

    // You can add logic here to detect staging vs production
    // For example, check bundle ID or API endpoint
    const bundleId = DeviceInfo.getBundleId();

    if (bundleId.includes('.staging')) {
      return 'staging';
    }

    return 'production';
  }

  /**
   * Compare two semantic versions
   * Returns: -1 if v1 < v2, 0 if equal, 1 if v1 > v2
   */
  compareVersions(v1: string, v2: string): number {
    const v1Parts = v1.split('.').map(Number);
    const v2Parts = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;

      if (v1Part > v2Part) return 1;
      if (v1Part < v2Part) return -1;
    }

    return 0;
  }

  /**
   * Check if current version meets minimum requirement
   */
  async meetsMinimumVersion(minimumVersion: string): Promise<boolean> {
    const currentVersion = (await this.getVersionInfo()).version;
    return this.compareVersions(currentVersion, minimumVersion) >= 0;
  }

  /**
   * Check if an update is available
   */
  isUpdateAvailable(currentVersion: string, latestVersion: string): boolean {
    return this.compareVersions(currentVersion, latestVersion) < 0;
  }

  /**
   * Get platform-specific version code
   */
  async getVersionCode(): Promise<number> {
    if (Platform.OS === 'android') {
      // Android versionCode
      const buildNumber = DeviceInfo.getBuildNumber();
      return parseInt(buildNumber, 10);
    } else {
      // iOS CFBundleVersion
      const buildNumber = DeviceInfo.getBuildNumber();
      return parseInt(buildNumber, 10);
    }
  }

  /**
   * Get user-agent string for API calls
   */
  async getUserAgent(): Promise<string> {
    const info = await this.getVersionInfo();
    const systemVersion = DeviceInfo.getSystemVersion();
    const model = DeviceInfo.getModel();

    return `${info.appName}/${info.version} (${Platform.OS} ${systemVersion}; ${model}) Build/${info.buildNumber}`;
  }
}

export default new VersionConfig();
